import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, ArrowLeft, Sparkles, Check, Shield } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Signup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');
  
  const { signUp, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [inviteInfo, setInviteInfo] = useState(null);
  const [inviteError, setInviteError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  // Check invite token validity
  useEffect(() => {
    if (inviteToken) {
      axios.get(`${API}/auth/invite/${inviteToken}`)
        .then(res => {
          setInviteInfo(res.data);
          if (res.data.email) {
            setFormData(prev => ({ ...prev, email: res.data.email }));
          }
        })
        .catch(err => {
          setInviteError(err.response?.data?.detail || 'Invalid invite link');
        });
    }
  }, [inviteToken]);

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validatePassword = (password) => {
    const checks = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password)
    };
    return checks;
  };

  const passwordChecks = validatePassword(formData.password);
  const isPasswordValid = Object.values(passwordChecks).every(Boolean);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (!isPasswordValid) {
      toast.error('Password does not meet requirements');
      return;
    }

    setLoading(true);

    const { data, error } = await signUp(
      formData.email, 
      formData.password, 
      { name: formData.name },
      inviteToken // Pass invite token
    );
    
    if (error) {
      toast.error(error);
    } else if (data?.user) {
      if (inviteInfo?.role === 'admin') {
        toast.success('Admin account created! Please check your email to verify your account.');
      } else {
        toast.success('Account created! Please check your email to verify your account.');
      }
      navigate('/login');
    }
    
    setLoading(false);
  };

  const PasswordCheck = ({ passed, text }) => (
    <div className={`flex items-center gap-2 text-xs ${passed ? 'text-green-600' : 'text-[#737373]'}`}>
      <div className={`w-4 h-4 rounded-full flex items-center justify-center ${passed ? 'bg-green-100' : 'bg-[#F5F5F5]'}`}>
        {passed && <Check className="w-3 h-3" />}
      </div>
      {text}
    </div>
  );

  // Show error if invite is invalid
  if (inviteToken && inviteError) {
    return (
      <div className="min-h-screen pt-28 flex items-center justify-center px-6" data-testid="invite-error">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Shield className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="font-serif text-2xl font-semibold text-[#0A0A0A] mb-2">Invalid Invite</h1>
          <p className="text-[#737373] mb-6">{inviteError}</p>
          <Button asChild className="rounded-none bg-[#0A0A0A] hover:bg-[#262626]">
            <Link to="/signup">Sign Up Without Invite</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 flex" data-testid="signup-page">
      {/* Left - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          <Link 
            to="/" 
            className="inline-flex items-center text-sm text-[#737373] hover:text-[#0A0A0A] mb-8"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>

          {/* Invite Badge */}
          {inviteInfo && (
            <div className="mb-6 p-4 bg-[#C9A962]/10 border border-[#C9A962]/30">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#C9A962]" />
                <span className="text-sm font-medium text-[#0A0A0A]">
                  You've been invited as {inviteInfo.role === 'admin' ? 'an Admin' : 'a User'}
                </span>
              </div>
              <p className="text-xs text-[#737373] mt-1">
                Complete your registration to activate your account
              </p>
            </div>
          )}

          <div className="mb-8">
            <h1 className="font-serif text-3xl font-semibold text-[#0A0A0A]">
              {inviteInfo ? 'Accept Invitation' : 'Create Account'}
            </h1>
            <p className="text-[#737373] mt-2">
              {inviteInfo ? 'Complete your registration to join the team' : 'Join us and elevate your corporate branding'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="name" className="text-sm text-[#737373]">Full Name</Label>
              <Input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="John Doe"
                className="mt-2 h-12 rounded-none border-[#E5E5E5] focus:border-[#0A0A0A] focus:ring-[#0A0A0A]"
                data-testid="signup-name"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-sm text-[#737373]">Email Address</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="you@example.com"
                className="mt-2 h-12 rounded-none border-[#E5E5E5] focus:border-[#0A0A0A] focus:ring-[#0A0A0A]"
                data-testid="signup-email"
                readOnly={inviteInfo?.email ? true : false}
              />
              {inviteInfo?.email && (
                <p className="text-xs text-[#737373] mt-1">Email is set by the invite</p>
              )}
            </div>

            <div>
              <Label htmlFor="password" className="text-sm text-[#737373]">Password</Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  placeholder="Create a strong password"
                  className="h-12 rounded-none border-[#E5E5E5] focus:border-[#0A0A0A] focus:ring-[#0A0A0A] pr-12"
                  data-testid="signup-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#737373] hover:text-[#0A0A0A]"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              
              {formData.password && (
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <PasswordCheck passed={passwordChecks.length} text="8+ characters" />
                  <PasswordCheck passed={passwordChecks.uppercase} text="Uppercase letter" />
                  <PasswordCheck passed={passwordChecks.lowercase} text="Lowercase letter" />
                  <PasswordCheck passed={passwordChecks.number} text="Number" />
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="text-sm text-[#737373]">Confirm Password</Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                placeholder="Confirm your password"
                className="mt-2 h-12 rounded-none border-[#E5E5E5] focus:border-[#0A0A0A] focus:ring-[#0A0A0A]"
                data-testid="signup-confirm-password"
              />
              {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
              )}
            </div>

            <Button
              type="submit"
              disabled={loading || !isPasswordValid}
              className="w-full h-12 bg-[#0A0A0A] text-white hover:bg-[#262626] rounded-none btn-lift disabled:opacity-50"
              data-testid="signup-submit"
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>

            <p className="text-xs text-[#737373] text-center">
              By creating an account, you agree to our{' '}
              <Link to="/terms" className="text-[#0A0A0A] hover:underline">Terms of Service</Link>
              {' '}and{' '}
              <Link to="/privacy" className="text-[#0A0A0A] hover:underline">Privacy Policy</Link>
            </p>
          </form>

          <p className="mt-8 text-center text-[#737373]">
            Already have an account?{' '}
            <Link to="/login" className="text-[#0A0A0A] font-medium hover:text-[#C9A962]">
              Sign in
            </Link>
          </p>
        </div>
      </div>

      {/* Right - Branding */}
      <div className="hidden lg:flex w-1/2 bg-[#0A0A0A] items-center justify-center p-16">
        <div className="max-w-md text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 border border-[#C9A962]/30 text-[#C9A962] text-xs uppercase tracking-widest mb-8">
            <Sparkles className="w-3 h-3" />
            Join The Elite
          </div>
          <h2 className="font-serif text-4xl font-semibold text-white leading-tight">
            Premium Branding
            <span className="block text-[#C9A962]">At Your Fingertips</span>
          </h2>
          <p className="mt-6 text-[#A3A3A3]">
            Create an account to access exclusive products, save your favorites, and enjoy a seamless shopping experience.
          </p>
          
          <div className="mt-10 space-y-4 text-left">
            {['Track your orders in real-time', 'Save items to your wishlist', 'Exclusive member offers'].map((benefit) => (
              <div key={benefit} className="flex items-center gap-3">
                <div className="w-5 h-5 bg-[#C9A962]/20 rounded-full flex items-center justify-center">
                  <Check className="w-3 h-3 text-[#C9A962]" />
                </div>
                <span className="text-white/80 text-sm">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
